import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { sendEmailBlast, normalizeContactForEmail } from "../../../../services/email";
import { getContactsList } from "../../../../services/contacts";
import {
  CONTACT_SOURCES,
  DEFAULT_FIELD_MAPPINGS,
  DEFAULT_EMAIL_PAYLOAD,
  DEFAULT_EMAIL_BINDINGS,
  PAGINATION_DEFAULTS,
} from "../constants/blastConstants";
import { validateContacts } from "../utils/fieldValidation";

const BlastCampaignContext = createContext();

const initialState = {
  // Tenant
  tenantId: null,

  // Contact source mode: "csv" or "database"
  contactSource: CONTACT_SOURCES.CSV,

  // CSV Mode State
  fileUploaded: null,
  fileUploadedHeaders: [],
  csvParsedContacts: [],
  fieldMappings: { ...DEFAULT_FIELD_MAPPINGS },
  isCsvMapped: false,

  // Database Mode State
  databaseContacts: [],
  databaseContactsMeta: null,
  selectedContactIds: new Set(),
  databaseSearchTerm: "",
  databaseFilters: {},
  databaseCurrentPage: PAGINATION_DEFAULTS.INITIAL_PAGE,

  // Final contacts for blast (normalized from either source)
  blastContacts: [],

  // Email template
  emailPayload: [...DEFAULT_EMAIL_PAYLOAD],
  emailBindings: [...DEFAULT_EMAIL_BINDINGS],

  // UI state
  isLoading: false,
  isLoadingContacts: false,
  isSuccess: false,
  error: null,
  validationErrors: [],

  // Results
  emailsSent: [],
  emailsFailed: [],
};

export const BlastCampaignProvider = ({ children }) => {
  const [state, setState] = useState(initialState);

  // Race condition prevention
  const databaseFetchIdRef = useRef(0);

  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // ==================== CONTACT SOURCE ====================

  const setContactSource = useCallback((source) => {
    updateState({
      contactSource: source,
      blastContacts: [],
      validationErrors: [],
    });
  }, [updateState]);

  // ==================== CSV MODE ACTIONS ====================

  const setCsvFile = useCallback((file, headers, parsedContacts) => {
    updateState({
      fileUploaded: file,
      fileUploadedHeaders: headers,
      csvParsedContacts: parsedContacts,
      fieldMappings: { ...DEFAULT_FIELD_MAPPINGS },
      isCsvMapped: false,
      blastContacts: [],
      validationErrors: [],
    });
  }, [updateState]);

  const setFieldMapping = useCallback((systemField, csvColumn) => {
    setState((prev) => ({
      ...prev,
      fieldMappings: {
        ...prev.fieldMappings,
        [systemField]: csvColumn,
      },
    }));
  }, []);

  const setFieldMappings = useCallback((mappings) => {
    setState((prev) => ({
      ...prev,
      fieldMappings: {
        ...prev.fieldMappings,
        ...mappings,
      },
    }));
  }, []);

  const applyCsvMappings = useCallback(() => {
    setState((prev) => {
      const mappedContacts = prev.csvParsedContacts.map((contact) => {
        const normalizedContact = normalizeContactForEmail(
          contact,
          CONTACT_SOURCES.CSV,
          prev.fieldMappings
        );
        return normalizedContact;
      });

      return {
        ...prev,
        blastContacts: mappedContacts,
        isCsvMapped: true,
      };
    });
  }, []);

  const clearCsvData = useCallback(() => {
    updateState({
      fileUploaded: null,
      fileUploadedHeaders: [],
      csvParsedContacts: [],
      fieldMappings: { ...DEFAULT_FIELD_MAPPINGS },
      isCsvMapped: false,
      blastContacts: [],
    });
  }, [updateState]);

  // ==================== DATABASE MODE ACTIONS ====================

  const fetchDatabaseContacts = useCallback(async (params = {}) => {
    const requestId = ++databaseFetchIdRef.current;
    updateState({ isLoadingContacts: true, error: null });

    try {
      const response = await getContactsList({
        page: params.page || state.databaseCurrentPage,
        page_size: params.page_size || PAGINATION_DEFAULTS.PAGE_SIZE,
        search: (params.search ?? state.databaseSearchTerm) || null,
        filters: (params.filters ?? state.databaseFilters) || null,
        order_by: ["createdAt"],
        order_direction: ["DESC"],
      });

      // Only update if this is the latest request
      if (requestId === databaseFetchIdRef.current) {
        if (response?.statusCode === 200) {
          updateState({
            databaseContacts: response.body || [],
            databaseContactsMeta: response.meta || null,
            isLoadingContacts: false,
          });
        } else {
          throw new Error(`Failed to fetch contacts: ${response?.statusCode}`);
        }
      }
    } catch (err) {
      if (requestId === databaseFetchIdRef.current) {
        console.error("Error fetching database contacts:", err);
        updateState({
          isLoadingContacts: false,
          error: err.message || "Failed to fetch contacts",
        });
      }
    }
  }, [updateState, state.databaseCurrentPage, state.databaseSearchTerm, state.databaseFilters]);

  const setDatabaseSearchTerm = useCallback((term) => {
    updateState({
      databaseSearchTerm: term,
      databaseCurrentPage: PAGINATION_DEFAULTS.INITIAL_PAGE,
    });
  }, [updateState]);

  const setDatabasePage = useCallback((page) => {
    updateState({ databaseCurrentPage: page });
  }, [updateState]);

  const toggleContactSelection = useCallback((contactId) => {
    setState((prev) => {
      const newSelected = new Set(prev.selectedContactIds);
      if (newSelected.has(contactId)) {
        newSelected.delete(contactId);
      } else {
        newSelected.add(contactId);
      }
      return { ...prev, selectedContactIds: newSelected };
    });
  }, []);

  const selectAllContacts = useCallback(() => {
    setState((prev) => {
      const allIds = prev.databaseContacts.map((c) => c.partyUuid);
      return { ...prev, selectedContactIds: new Set(allIds) };
    });
  }, []);

  const deselectAllContacts = useCallback(() => {
    updateState({ selectedContactIds: new Set() });
  }, [updateState]);

  const applyDatabaseSelection = useCallback(() => {
    setState((prev) => {
      const selectedContacts = prev.databaseContacts
        .filter((c) => prev.selectedContactIds.has(c.partyUuid))
        .map((contact) => normalizeContactForEmail(contact, CONTACT_SOURCES.DATABASE));

      return {
        ...prev,
        blastContacts: selectedContacts,
      };
    });
  }, []);

  // ==================== EMAIL TEMPLATE ACTIONS ====================

  const setEmailPayload = useCallback((payload) => {
    updateState({ emailPayload: payload });
  }, [updateState]);

  const setEmailBindings = useCallback((bindings) => {
    updateState({ emailBindings: bindings });
  }, [updateState]);

  const updateEmailField = useCallback((field, value) => {
    setState((prev) => ({
      ...prev,
      emailPayload: prev.emailPayload.map((item) =>
        item.field === field ? { ...item, value } : item
      ),
    }));
  }, []);

  // ==================== BLAST ACTION ====================

  const blast = useCallback(async () => {
    // Validate contacts
    if (!state.blastContacts.length) {
      updateState({
        error: "No contacts selected. Please add contacts before sending.",
      });
      return { success: false };
    }

    const validation = validateContacts(state.blastContacts);
    if (!validation.isValid) {
      updateState({
        validationErrors: validation.errors,
        error: `${validation.invalidContacts.length} contact(s) have invalid data`,
      });
      return { success: false };
    }

    updateState({ isLoading: true, error: null, validationErrors: [] });

    try {
      const response = await sendEmailBlast({
        tenantId: state.tenantId,
        emailSetup: {
          payload: state.emailPayload,
          binding: state.emailBindings,
        },
        contacts: state.blastContacts,
      });

      updateState({
        isLoading: false,
        isSuccess: true,
        emailsSent: response.emailSent ?? response.email_sent ?? [],
        emailsFailed: response.emailFailed ?? response.email_failed ?? [],
      });

      return { success: true, response };
    } catch (err) {
      console.error("Error sending email blast:", err);
      updateState({
        isLoading: false,
        error: err.message || "Failed to send email blast",
      });
      return { success: false, error: err };
    }
  }, [state, updateState]);

  // ==================== UTILITY ACTIONS ====================

  const clear = useCallback(() => {
    setState({ ...initialState, tenantId: state.tenantId });
    databaseFetchIdRef.current = 0;
  }, [state.tenantId]);

  const clearError = useCallback(() => {
    updateState({ error: null, validationErrors: [] });
  }, [updateState]);

  const clearResults = useCallback(() => {
    updateState({
      isSuccess: false,
      emailsSent: [],
      emailsFailed: [],
    });
  }, [updateState]);

  const setTenantId = useCallback((tenantId) => {
    updateState({ tenantId });
  }, [updateState]);

  // ==================== COMPUTED VALUES ====================

  const selectedContactsCount = useMemo(() => {
    if (state.contactSource === CONTACT_SOURCES.CSV) {
      return state.blastContacts.length;
    }
    return state.selectedContactIds.size;
  }, [state.contactSource, state.blastContacts.length, state.selectedContactIds.size]);

  const isReadyToBlast = useMemo(() => {
    const hasContacts = state.blastContacts.length > 0;
    const hasSubject = state.emailPayload.find((p) => p.field === "subject")?.value;
    const hasFrom = state.emailPayload.find((p) => p.field === "from")?.value;
    const hasBody = state.emailPayload.find((p) => p.field === "body")?.value;

    return hasContacts && hasSubject && hasFrom && hasBody;
  }, [state.blastContacts.length, state.emailPayload]);

  // ==================== CONTEXT VALUE ====================

  const value = {
    // State
    ...state,
    selectedContactIds: Array.from(state.selectedContactIds),

    // Computed
    selectedContactsCount,
    isReadyToBlast,

    // Actions
    updateState,
    setTenantId,
    setContactSource,

    // CSV actions
    setCsvFile,
    setFieldMapping,
    setFieldMappings,
    applyCsvMappings,
    clearCsvData,

    // Database actions
    fetchDatabaseContacts,
    setDatabaseSearchTerm,
    setDatabasePage,
    toggleContactSelection,
    selectAllContacts,
    deselectAllContacts,
    applyDatabaseSelection,

    // Email template actions
    setEmailPayload,
    setEmailBindings,
    updateEmailField,

    // Blast actions
    blast,

    // Utility actions
    clear,
    clearError,
    clearResults,
  };

  return (
    <BlastCampaignContext.Provider value={value}>
      {children}
    </BlastCampaignContext.Provider>
  );
};

export const useBlastCampaign = () => {
  const context = useContext(BlastCampaignContext);
  if (!context) {
    throw new Error("useBlastCampaign must be used within BlastCampaignProvider");
  }
  return context;
};
